import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
} from "@mui/material";
import BackButton from "../../../components/Controls/Buttons/BackArrowButton";
import Label from "../../../components/Label/Label";
import Buttons from "../../../components/Controls/Buttons/Button";
import DatePicker from "react-datepicker";
import * as api from "../../../redux/batches/actions.js";
import { useDispatch, useSelector } from "react-redux";
import CustomizedSnackbars from "../../../components/Snackbars/CustomizedSnackbars";
import { formatLocal } from "../../../utility";

// DateInput Component
const DateInput = ({ name, label, value, onChange, minDate, maxDate }) => {
  return (
    <DatePicker
      selected={value ? new Date(value) : null}
      showYearDropdown
      scrollableYearDropdown
      yearDropdownItemNumber={150}
      onChange={(date) => {
        onChange({
          target: {
            name,
            value: date,
          },
        });
      }}
      dateFormat="dd-MMM-yyyy"
      name={name}
      minDate={minDate}
      maxDate={maxDate}
      customInput={
        <TextField
          label={label}
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      }
    />
  );
};

// Reusable MultiSelect Component
const MultiSelectField = ({
  label,
  name,
  value,
  options = [],
  optionValue,
  optionLabel,
  onChange,
  disabled = false,
}) => {
  return (
    <>
      <Typography fontSize={14} fontWeight={600} mb={0.5}>
        {label}
      </Typography>

      <TextField
        select
        disabled={disabled}
        SelectProps={{
          multiple: true,
          renderValue: (selected) =>
            options
              .filter((item) => selected.includes(item[optionValue]))
              .map((item) => item[optionLabel])
              .join(", "),
        }}
        fullWidth
        size="small"
        name={name}
        value={value}
        onChange={onChange}
        sx={{ mb: 2 }}
      >
        {options.map((item) => (
          <MenuItem
            key={item[optionValue]}
            value={item[optionValue]}
            sx={{
              minHeight: 10,
              py: 0.5,
            }}
          >
            <Checkbox checked={value.includes(item[optionValue])} />
            <ListItemText
              primary={`${item?.product_name || ""}${item?.site_name ? " - " + item?.site_name : ""
                } ${item?.step_name ? " - " + item?.step_name : ""}`}
            />
          </MenuItem>
        ))}
      </TextField>
    </>
  );
};

// Main Popup Component
const DownloadBatchesPopup = ({ open, onClose, canDownload = false }) => {
  const [formData, setFormData] = useState({
    product: [],
    site: [],
    unitOperation: [],
    batchRange: "",
    start_date: "",
    end_date: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      let updated = { ...prev, [name]: value };

      if (name === "product") {
        updated.site = [];
        updated.unitOperation = [];
      }
      if (name === "site") {
        updated.unitOperation = [];
      }
      return updated;
    });
  };

  const handleReset = () => {
    setFormData({
      product: [],
      site: [],
      unitOperation: [],
      batchRange: "",
      start_date: "",
      end_date: "",
    });
  };

  const dispatch = useDispatch();
  const batchesSlice = useSelector((state) => state.batch || {});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const triggerDownload = (url, filename = "batch_export.csv") => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Poll API for export status
  const pollStatus = async (taskId, retryCount = 0) => {
    const MAX_RETRIES = 20;
    try {
      const statusRes = await dispatch(api.getBatchesStatus(taskId));
      const status = statusRes?.data?.status;

      if (status === "completed") {
        const downloadUrl = statusRes?.data?.download_url;
        if (downloadUrl) triggerDownload(downloadUrl, `batch_${taskId}.csv`);
        showSnackbar("Export completed successfully!", "success");
        setLoading(false);
        setTimeout(() => {
          handleReset();
          onClose();
        }, 3000);
        return;
      }

      if (status === "failed") {
        showSnackbar("Batch processing failed!", "error");
        setLoading(false);
        return;
      }

      if (status === "pending") {
        if (retryCount >= MAX_RETRIES) {
          showSnackbar("Max retries reached. Export failed!", "error");
          setLoading(false);
          return;
        }
        await delay(3000);
        return pollStatus(taskId, retryCount + 1);
      }
    } catch (error) {
      console.error("Error checking status:", error);
      showSnackbar("Error checking batch status!", "error");
      setLoading(false);
    }
  };

  // Start export with correct payload
  const startExport = async (batchesPayload) => {
    try {
      const exportRes = await dispatch(api.expoortBatches({ batches: batchesPayload }));

      if (!exportRes?.data?.status) {
        showSnackbar("Export API failed!", "error");
        setLoading(false);
        return;
      }

      const taskId = exportRes?.data?.data?.task_id;
      if (!taskId) {
        showSnackbar("Task ID not received!", "error");
        setLoading(false);
        return;
      }

      await pollStatus(taskId);
    } catch (error) {
      console.error("Error starting export:", error);
      showSnackbar("Error starting export!", "error");
      setLoading(false);
    }
  };

  // Handle download click
  const handleDownload = async () => {
    if (!canDownload) return;
    if (!formData.unitOperation.length || !formData.batchRange) {
      showSnackbar("Please select unit operation and batch range", "warning");
      return;
    }

    setLoading(true);

    try {
      let res;
      if (formData.batchRange === "custom") {
        res = await dispatch(
          api.getBatchesIds({
            unit_op_ids: formData.unitOperation.join(","),
            start_date: formatLocal(formData.start_date),
            end_date: formatLocal(formData.end_date),
          })
        );
      } else {
        res = await dispatch(
          api.getBatchesIds({
            unit_op_ids: formData.unitOperation.join(","),
            filter: formData.batchRange,
          })
        );
      }

      const batchesData = res?.data || [];

      if (!batchesData.length) {
        showSnackbar("No batches found", "info");
        setLoading(false);
        return;
      }

      // Group batches by unit_op_id
      const batchesPayload = formData.unitOperation.map((uopId) => {
        const batchForUop = (batchesData || [])
          .filter((b) => b.unit_op_id === uopId)
          .map((b) => b.batch_id); // flatten in case batch_id is an array

        return { unit_op_id: uopId, batch_id: batchForUop };
      });

      await startExport(batchesPayload);
    } catch (error) {
      console.error(error);
      showSnackbar("Error fetching batch IDs!", "error");
      setLoading(false);
    }
  };

  // Fetch products when open
  useEffect(() => {
    if (open && canDownload) dispatch(api.getProductMetaList());
  }, [canDownload, dispatch, open]);

  // Fetch sites when product changes
  useEffect(() => {
    if (canDownload && formData.product.length > 0) {
      dispatch(api.getSiteMetaList({ product_ids: formData.product.join(",") }));
    }
  }, [canDownload, dispatch, formData.product]);

  // Fetch unit operations when site changes
  useEffect(() => {
    if (canDownload && formData.product.length > 0 && formData.site.length > 0) {
      dispatch(
        api.getUnitOperationMetaList({
          product_ids: formData.product.join(","),
          site_ids: formData.site.join(","),
        })
      );
    }
  }, [canDownload, dispatch, formData.product, formData.site]);

  if (!canDownload) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      keepMounted={false}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0,0,0,0.3)", // dark overlay
            backdropFilter: "blur(1px)",        // 🔥 blur effect
          },
        },
      }}
      PaperProps={{
        sx: {
          position: "fixed",
          bottom: 0,
          right: 16,
          m: 0,
          width: 400,
          maxHeight: "calc(100% - 142px)",
          height: "100%",
          boxShadow: "none",
          border: "1px solid #ccc",
          p: 1,
          transition: "width 0.3s ease",
        },
      }}
    >
      {/* Header */}
      <DialogTitle className="dialog-title">
        <Label text="Download Batches" color="#fff" fontSize="14px" fontWeight="500" />
        <BackButton onClick={onClose} />
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 1, mt: 1 }}>
        <MultiSelectField
          label="Product"
          name="product"
          value={formData.product}
          options={batchesSlice?.productMetaList?.data || []}
          optionValue="product_id"
          optionLabel="product_name"
          onChange={handleChange}
        />

        <MultiSelectField
          label="Manufacturing Site"
          name="site"
          value={formData.site}
          options={batchesSlice?.siteMetaList?.data || []}
          optionValue="site_id"
          optionLabel="site_name"
          disabled={!formData?.product?.length}
          onChange={handleChange}
        />

        <MultiSelectField
          label="Unit Operation"
          name="unitOperation"
          value={formData.unitOperation}
          options={batchesSlice?.mfgMetaList?.data || []}
          optionValue="unit_op_id"
          optionLabel="step_name"
          onChange={handleChange}
          disabled={!formData?.product?.length || !formData?.site?.length}
        />

        <Typography fontSize={14} fontWeight={600} mb={1}>
          Batch Range
        </Typography>

        <RadioGroup name="batchRange" value={formData.batchRange} onChange={handleChange}>
          <FormControlLabel
            value="recent_50"
            control={<Radio size="small" />}
            label={<Box sx={{ border: "1px solid #ddd", p: 1, borderRadius: "4px", width: "100%" }}>Recent 50 Batches</Box>}
            sx={{ alignItems: "flex-start", mb: 1 }}
          />

          <FormControlLabel
            value="recent_30_days"
            control={<Radio size="small" />}
            label={<Box sx={{ border: "1px solid #ddd", p: 1, borderRadius: "4px", width: "100%" }}>Recent 30 Days</Box>}
            sx={{ alignItems: "flex-start", mb: 1 }}
          />

          <FormControlLabel
            value="custom"
            control={<Radio size="small" />}
            label={
              <Box sx={{ border: "1px solid #ddd", p: 1, borderRadius: "4px", width: "100%" }}>
                <Box display="flex" gap={1}>
                  <DateInput label="From" name="start_date" value={formData.start_date} onChange={handleChange} maxDate={formData.end_date} />
                  <DateInput label="To" name="end_date" value={formData.end_date} onChange={handleChange} maxDate={new Date()} />
                </Box>
              </Box>
            }
            sx={{ alignItems: "flex-start" }}
          />
        </RadioGroup>
      </DialogContent>

      {snackbar.open && (
        <CustomizedSnackbars
          severity={snackbar.severity}
          state={snackbar.open}
          text={snackbar.message}
          handleClose={() => setSnackbar({ open: false, message: "", severity: "" })}
        />
      )}

      {/* Footer */}
      <DialogActions sx={{ justifyContent: "flex-end", p: 1, borderTop: "1px solid #ccc" }}>
        <Buttons size="small" colors="outline-gray" text="Reset" onClick={handleReset} />
        <Buttons size="small" text="Download" onClick={handleDownload} loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

export default DownloadBatchesPopup;
